import { useCallback, useEffect, useMemo, useState } from 'react';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import {
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { stopPortalEvents } from './portalGuard';
import { buildSnoozePresets, toDateInput } from './snoozePresets';

export interface SnoozeMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** Always a `YYYY-MM-DD`. The server resolves 09:00 in the org's timezone. */
  onSelect: (untilDate: string) => void;
  /** Frozen clock for stories and tests. Defaults to now. */
  today?: Date;
}

const ITEM_SX = { fontSize: '0.8125rem', minHeight: 32, py: 0 } as const;

/**
 * Snooze until, as three presets and a date (GDN-3380).
 *
 * Presets are dates, never instants: see `snoozePresets.ts` for why the client
 * must not compute the 09:00 itself. The hints on the right ("Fri, 9:00 AM",
 * "Mon 7 Sep") are the whole reason presets beat a bare date field. A biller
 * choosing "Next week" is choosing a Monday, and the menu says which one
 * before they commit rather than after.
 *
 * "Pick a date" is a native date input rather than the MUI picker on purpose.
 * The MUI picker needs a LocalizationProvider that only the app shell mounts,
 * so it renders in the app and throws in Storybook and in any test that has
 * not built the whole provider stack. The native control gives a
 * `YYYY-MM-DD` string with no parsing, no adapter, and no timezone step, which
 * is exactly what travels on the wire.
 */
export function SnoozeMenu({ anchorEl, open, onClose, onSelect, today }: SnoozeMenuProps) {
  const [picking, setPicking] = useState(false);
  const [pickedDate, setPickedDate] = useState('');
  /**
   * The clock is read when the menu OPENS, not when it mounts. The menu is
   * mounted with its row, and a workbench left open overnight would otherwise
   * keep offering a "Tomorrow" that is today.
   */
  const [openedAt, setOpenedAt] = useState(() => today ?? new Date());
  const now = today ?? openedAt;
  const presets = useMemo(() => buildSnoozePresets(now), [now]);
  const earliest = useMemo(() => {
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    return toDateInput(tomorrow);
  }, [now]);

  /* A reopened menu starts on the presets with a fresh clock, not wherever and
     whenever it was left. */
  useEffect(() => {
    if (open) {
      setOpenedAt(new Date());
    } else {
      setPicking(false);
      setPickedDate('');
    }
  }, [open]);

  const choose = useCallback(
    (date: string) => {
      onSelect(date);
      onClose();
    },
    [onClose, onSelect],
  );

  return (
    <Menu
      {...stopPortalEvents}
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      slotProps={{
        paper: {
          sx: theme => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1.5,
            boxShadow: '0 10px 30px rgba(15,23,42,0.14)',
            minWidth: 220,
          }),
        },
      }}
    >
      <Typography
        sx={{
          color: 'text.secondary',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.06em',
          px: 2,
          py: 1,
          textTransform: 'uppercase',
        }}
      >
        Snooze until
      </Typography>
      {presets.map(preset => (
        <MenuItem key={preset.key} onClick={() => choose(preset.date)} sx={ITEM_SX}>
          <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }}>
            {preset.label}
          </ListItemText>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', ml: 3 }}>
            {preset.hint}
          </Typography>
        </MenuItem>
      ))}
      <Divider />
      {picking ? (
        <Box sx={{ px: 2, py: 1 }}>
          {/**
           * DELIBERATELY NOT COMMITTED ON CHANGE. A native date input reports a
           * value the moment all three segments are filled, so a biller typing
           * the year digit by digit passes through 0002-09-10 — and a commit on
           * change would close the menu and snooze the encounter to the year 2
           * before they reached the 6. The value is held here and sent by Snooze.
           *
           * `min` is validity metadata only; the browser will not stop the
           * event, so the button carries the guard as well.
           */}
          <TextField
            autoFocus
            fullWidth
            size="small"
            type="date"
            label="Pick a date"
            value={pickedDate}
            slotProps={{
              htmlInput: { 'aria-label': 'Pick a date', min: earliest },
              inputLabel: { shrink: true },
            }}
            onChange={event => setPickedDate(event.target.value)}
            /* MUI's MenuList treats Arrow/Home/End on any descendant as list
               navigation and preventDefaults them, which is how a date input
               inside a menu loses the arrow keys that drive it. */
            onKeyDown={event => {
              event.stopPropagation();
              if (event.key === 'Enter' && pickedDate >= earliest) choose(pickedDate);
            }}
          />
          <Stack direction="row" sx={{ justifyContent: 'flex-end', mt: 1 }}>
            <Button
              disabled={!pickedDate || pickedDate < earliest}
              onClick={() => choose(pickedDate)}
              size="small"
              sx={{ textTransform: 'none' }}
              variant="contained"
            >
              Snooze
            </Button>
          </Stack>
        </Box>
      ) : (
        <MenuItem onClick={() => setPicking(true)} sx={ITEM_SX}>
          <ListItemIcon sx={{ minWidth: 30 }}>
            <CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }}>
            Pick a date
          </ListItemText>
        </MenuItem>
      )}
    </Menu>
  );
}
